import { StarIcon as StarIconOutline, XMarkIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import { useId, useState } from "react";
import { useDebounce } from "use-debounce";

import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@fronterahealth/frontera-ui-components/ui/combobox";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@fronterahealth/frontera-ui-components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@fronterahealth/frontera-ui-components/ui/field";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from "@fronterahealth/frontera-ui-components/ui/item";

import { ReportReviewerKind, useGetReviewerCandidatesQuery } from "@api/graphql/types-and-hooks";
import { PersonAvatar } from "@components/QaReview/components/PersonAvatar";
import { useCanSetFinalReviewer } from "@components/ReportReview/hooks/useCanManageReview";
import { useMyAuth0Id } from "@components/ReportReview/hooks/useMyAuth0Id";
import { useReportReviewers } from "@components/ReportReview/hooks/useReportReviewers";
import { useReviewRequest } from "@components/ReportReview/hooks/useReviewRequest";
import {
  INCLUDE_INTERNAL_REVIEWERS,
  REVIEWER_ROLES,
  ReviewerPerson,
  toCandidates,
} from "@components/ReportReview/reviewers";

type PickedReviewer = { person: ReviewerPerson; type: ReportReviewerKind };

const PersonLabel: React.FC<{ person: ReviewerPerson }> = ({ person }) => (
  <>
    <PersonAvatar id={person.id} initials={person.initials} size="default" />
    <span className="min-w-0">
      <span className="block truncate text-sm font-medium">{person.name}</span>
      {person.role && <span className="block truncate text-xs opacity-70">{person.role}</span>}
    </span>
  </>
);

export const ReviewRequestForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const myId = useMyAuth0Id();
  const { reviewers } = useReportReviewers();
  const { request, isPending } = useReviewRequest(onClose);
  const canSetFinalApprover = useCanSetFinalReviewer();
  const searchId = useId();
  const title = reviewers.length ? "Add reviewers" : "Request review";

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search.trim(), 200);
  const [picked, setPicked] = useState<Record<string, PickedReviewer>>({});

  const excludedIds = [
    ...reviewers.flatMap(({ reviewer }) => (reviewer ? [reviewer.id] : [])),
    ...(myId ? [myId] : []),
  ];
  const candidatesQuery = useGetReviewerCandidatesQuery({
    first: 20,
    includeInternal: INCLUDE_INTERNAL_REVIEWERS,
    where: {
      id: excludedIds.length ? { _nin: excludedIds } : undefined,
      role: { _in: REVIEWER_ROLES },
      name: debouncedSearch ? { _ilike: `%${debouncedSearch}%` } : undefined,
    },
  });
  const candidates = toCandidates(candidatesQuery.data);
  const pickedPeople = Object.values(picked).map(({ person }) => person);

  const setPeople = (people: ReviewerPerson[]) =>
    setPicked((current) =>
      Object.fromEntries(
        people.map((person) => [person.id, current[person.id] ?? { person, type: ReportReviewerKind.Reviewer }]),
      ),
    );

  const toggleFinalApprover = (id: string) =>
    setPicked((current) =>
      Object.fromEntries(
        Object.entries(current).map(([key, pick]) => [
          key,
          {
            ...pick,
            type:
              key === id && pick.type === ReportReviewerKind.Reviewer
                ? ReportReviewerKind.FinalApprover
                : ReportReviewerKind.Reviewer,
          },
        ]),
      ),
    );

  const submit = () => request(Object.entries(picked).map(([userId, { type }]) => ({ userId, type })));

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>Choose who should review this report.</DialogDescription>
      </DialogHeader>

      <Field>
        <FieldLabel htmlFor={searchId}>Reviewers</FieldLabel>
        <Combobox<ReviewerPerson, true>
          multiple
          filter={null}
          items={candidates}
          value={pickedPeople}
          onValueChange={setPeople}
          itemToStringLabel={(person) => person.name}
          isItemEqualToValue={(a, b) => a.id === b.id}
          inputValue={search}
          onInputValueChange={(query, { reason }) => {
            if (reason === "item-press") return;
            setSearch(query);
          }}
        >
          <ComboboxInput id={searchId} placeholder="Search people" showTrigger={false} showClear />
          <ComboboxContent>
            <ComboboxEmpty>{candidatesQuery.isLoading ? "Searching..." : "No people found."}</ComboboxEmpty>
            <ComboboxList>
              {(person: ReviewerPerson) => (
                <ComboboxItem key={person.id} value={person}>
                  <PersonLabel person={person} />
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </Field>

      {!!pickedPeople.length && (
        <ItemGroup className="max-h-64 gap-2 overflow-y-auto">
          {pickedPeople.map((person) => {
            const isFinalApprover = picked[person.id].type === ReportReviewerKind.FinalApprover;
            return (
              <Item key={person.id} variant="outline" size="sm">
                <ItemMedia>
                  <PersonAvatar id={person.id} initials={person.initials} size="default" />
                </ItemMedia>
                <ItemContent>
                  <ItemTitle>{person.name}</ItemTitle>
                  {person.role && <ItemDescription>{person.role}</ItemDescription>}
                </ItemContent>
                <ItemActions>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!canSetFinalApprover}
                    aria-label={`Final approver: ${person.name}`}
                    aria-pressed={isFinalApprover}
                    onClick={() => toggleFinalApprover(person.id)}
                  >
                    {isFinalApprover ? <StarIcon className="size-4" /> : <StarIconOutline className="size-4" />}
                    Final approver
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${person.name}`}
                    onClick={() => setPeople(pickedPeople.filter(({ id }) => id !== person.id))}
                  >
                    <XMarkIcon className="size-4" />
                  </Button>
                </ItemActions>
              </Item>
            );
          })}
        </ItemGroup>
      )}

      {!!Object.keys(picked).length && (
        <FieldDescription>Optional. Mark one reviewer as the final approver.</FieldDescription>
      )}

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} size="lg">
          Cancel
        </Button>
        <Button disabled={!Object.keys(picked).length || isPending} onClick={submit} size="lg">
          {title}
        </Button>
      </DialogFooter>
    </>
  );
};
